import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, apiError, API_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, UrgencyBadge } from "@/pages/portal/badges";
import { Attachments } from "@/pages/portal/Attachments";

const TEST_TYPES = [
  "Echocardiogram",
  "12-Lead ECG",
  "Holter Monitoring (24 hrs – 28 days)",
  "24-hr Ambulatory BP (Beat-to-Beat)",
  "Bespoke / Multi-Modality Protocol",
];
const URGENCY = ["Routine", "Urgent", "Emergency"];
const STATUSES = ["Pending", "Under Review", "Scheduled", "Completed"];

const inputCls =
  "w-full bg-white border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-gold transition-colors";
const labelCls = "font-mono text-[10px] tracking-[0.25em] uppercase text-navy/50 block mb-2";

export default function Referrals() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [specialists, setSpecialists] = useState([]);
  const [data, setData] = useState({ sent: [], received: [] });
  const [tab, setTab] = useState("received");
  const [form, setForm] = useState({
    to_user_id: searchParams.get("to") || "",
    patient_initials: "",
    test_type: TEST_TYPES[0],
    urgency: "Routine",
    clinical_notes: "",
  });
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const draftWithAi = async () => {
    setDrafting(true);
    try {
      const resp = await fetch(`${API_URL}/ai/referral-draft`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("scd_token")}`,
        },
        body: JSON.stringify({
          test_type: form.test_type,
          urgency: form.urgency,
          patient_initials: form.patient_initials,
          rough_notes: form.clinical_notes,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "AI draft failed");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      setForm((p) => ({ ...p, clinical_notes: "" }));
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") break;
          const parsed = JSON.parse(payload);
          if (parsed.error) throw new Error(parsed.error);
          if (parsed.delta) {
            setForm((p) => ({ ...p, clinical_notes: p.clinical_notes + parsed.delta }));
          }
        }
      }
      toast.success("AI draft ready — review before sending");
    } catch (err) {
      toast.error(err.message || "AI draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const load = () => {
    api.get("/referrals").then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/specialists").then((r) => setSpecialists(r.data)).catch(() => {});
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/referrals", form);
      toast.success("Referral sent to " + (specialists.find((s) => s.id === form.to_user_id)?.name || "colleague"));
      setForm({ ...form, patient_initials: "", clinical_notes: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/referrals/${id}`, { status });
      toast.success(`Referral marked ${status}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const list = tab === "sent" ? data.sent : data.received;

  return (
    <div data-testid="referrals-page">
      <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-2">Referral Exchange</p>
      <h1 className="font-serif text-3xl font-semibold text-navy mb-10">Referrals</h1>

      <div className="grid lg:grid-cols-5 gap-8 items-start">
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-navy/10 p-7 space-y-5" data-testid="new-referral-form">
          <h2 className="font-serif text-xl font-semibold text-navy">New Referral</h2>
          <div>
            <label className={labelCls}>To Specialist</label>
            <select
              required
              value={form.to_user_id}
              onChange={(e) => setForm({ ...form, to_user_id: e.target.value })}
              data-testid="referral-specialist-select"
              className={inputCls}
            >
              <option value="">Select a colleague…</option>
              {specialists
                .filter((s) => s.id !== user?.id)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} — {s.specialty}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Patient Initials</label>
            <input
              required
              maxLength={10}
              value={form.patient_initials}
              onChange={(e) => setForm({ ...form, patient_initials: e.target.value })}
              placeholder="e.g. J.B."
              data-testid="referral-initials-input"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Investigation</label>
            <select
              value={form.test_type}
              onChange={(e) => setForm({ ...form, test_type: e.target.value })}
              data-testid="referral-test-type-select"
              className={inputCls}
            >
              {TEST_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Urgency</label>
            <div className="grid grid-cols-3 gap-2" data-testid="referral-urgency-group">
              {URGENCY.map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => setForm({ ...form, urgency: u })}
                  data-testid={`urgency-${u.toLowerCase()}-button`}
                  className={`font-mono text-[10px] tracking-[0.15em] uppercase py-2.5 border transition-colors ${
                    form.urgency === u
                      ? "bg-navy text-ivory border-navy"
                      : "bg-white text-navy/50 border-navy/15 hover:border-gold"
                  }`}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`${labelCls} !mb-0`}>Clinical Notes</label>
              <button
                type="button"
                onClick={draftWithAi}
                disabled={drafting}
                data-testid="ai-draft-button"
                className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-gold hover:text-navy transition-colors disabled:opacity-50"
              >
                <Sparkles size={12} /> {drafting ? "Drafting…" : "Draft with AI"}
              </button>
            </div>
            <textarea
              rows={4}
              value={form.clinical_notes}
              onChange={(e) => setForm({ ...form, clinical_notes: e.target.value })}
              placeholder="Relevant history, current medication, clinical question…"
              data-testid="referral-notes-input"
              className={inputCls}
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="referral-submit-button"
            className="w-full bg-navy text-ivory font-mono text-xs tracking-[0.25em] uppercase py-3.5 hover:bg-gold hover:text-navy transition-colors disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send Referral"}
          </button>
        </form>

        <div className="lg:col-span-3">
          <div className="flex gap-px bg-navy/10 border border-navy/10 mb-6 w-fit">
            {["received", "sent"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`referrals-tab-${t}`}
                className={`font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-3 transition-colors ${
                  tab === t ? "bg-navy text-ivory" : "bg-white text-navy/50 hover:text-navy"
                }`}
              >
                {t === "received" ? `Received (${data.received.length})` : `Sent (${data.sent.length})`}
              </button>
            ))}
          </div>

          {list.length === 0 ? (
            <div className="bg-white border border-navy/10 p-10 text-center" data-testid="referrals-empty">
              <p className="text-sm text-navy/50">No {tab} referrals yet.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="referrals-list">
              {list.map((r) => (
                <div key={r.id} className="bg-white border border-navy/10 p-6" data-testid={`referral-card-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-mono text-sm font-semibold text-navy">
                        PT {r.patient_initials} · {r.test_type}
                      </p>
                      <p className="text-xs text-navy/50 mt-1">
                        {r.from_name} ({r.from_organization || "—"}) → {r.to_name} ·{" "}
                        {new Date(r.created_at).toLocaleDateString("en-GB")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <UrgencyBadge urgency={r.urgency} />
                      <StatusBadge status={r.status} />
                    </div>
                  </div>
                  {r.clinical_notes && (
                    <p className="text-sm text-navy/60 leading-relaxed border-l-2 border-gold/50 pl-4 mb-4">
                      {r.clinical_notes}
                    </p>
                  )}
                  <Attachments referral={r} />
                  {tab === "received" && (
                    <div className="flex items-center gap-3 mt-2">
                      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-navy/40">
                        Update Status
                      </label>
                      <select
                        value={r.status}
                        onChange={(e) => setStatus(r.id, e.target.value)}
                        data-testid={`referral-status-select-${r.id}`}
                        className="bg-white border border-navy/15 px-3 py-2 text-xs text-navy focus:outline-none focus:border-gold"
                      >
                        {STATUSES.map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
